import React, { useState } from 'react';
import { 
  BarChart3, 
  Eye, 
  Volume2, 
  FileText, 
  Target, 
  TrendingUp, 
  Shield,
  Star,
  ChevronDown,
  ChevronUp,
  Zap,
  Award
} from 'lucide-react';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar, 
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';

const AdvancedAnalysisResults = ({ analysis, platform }) => {
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-800 bg-green-100 border border-green-200';
    if (score >= 5) return 'text-amber-800 bg-amber-100 border border-amber-200';
    return 'text-red-800 bg-red-100 border border-red-200';
  };
  
  const getScoreIcon = (score) => {
    if (score >= 8) return <Award className="w-4 h-4" />;
    if (score >= 5) return <Star className="w-4 h-4" />;
    return <Target className="w-4 h-4" />;
  };

  // Prepare radar chart data
  const radarData = [
    { category: 'Visual', score: analysis.visualAnalysis ? Object.values(analysis.visualAnalysis).reduce((sum, item) => sum + item.score, 0) / Object.keys(analysis.visualAnalysis).length : 7 },
    { category: 'Audio', score: analysis.audioAnalysis ? Object.values(analysis.audioAnalysis).reduce((sum, item) => sum + item.score, 0) / Object.keys(analysis.audioAnalysis).length : 7 },
    { category: 'Content', score: analysis.contentAnalysis ? Object.values(analysis.contentAnalysis).reduce((sum, item) => sum + item.score, 0) / Object.keys(analysis.contentAnalysis).length : 7 },
    { category: 'Platform', score: analysis.platformOptimization ? Object.values(analysis.platformOptimization).reduce((sum, item) => sum + item.score, 0) / Object.keys(analysis.platformOptimization).length : 7 },
    { category: 'Engagement', score: analysis.engagementPrediction ? Object.values(analysis.engagementPrediction).reduce((sum, item) => sum + item.score, 0) / Object.keys(analysis.engagementPrediction).length : 7 },
    { category: 'Compliance', score: analysis.brandCompliance ? Object.values(analysis.brandCompliance).reduce((sum, item) => sum + item.score, 0) / Object.keys(analysis.brandCompliance).length : 7 }
  ];

  // Prepare bar chart data for detailed scores
  const getDetailedScores = (category) => {
    if (!analysis[category]) return [];
    return Object.entries(analysis[category]).map(([key, value]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      score: value.score || 7,
      fullName: value.analysis || ''
    }));
  };

  const categories = [
    { 
      key: 'visualAnalysis', 
      title: 'Visual Analysis', 
      icon: <Eye className="w-5 h-5" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    { 
      key: 'audioAnalysis', 
      title: 'Audio Analysis', 
      icon: <Volume2 className="w-5 h-5" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    { 
      key: 'contentAnalysis', 
      title: 'Content Analysis', 
      icon: <FileText className="w-5 h-5" />,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    { 
      key: 'platformOptimization', 
      title: 'Platform Optimization', 
      icon: <Target className="w-5 h-5" />,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    },
    { 
      key: 'engagementPrediction', 
      title: 'Engagement Prediction', 
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200'
    },
    { 
      key: 'brandCompliance', 
      title: 'Brand Compliance', 
      icon: <Shield className="w-5 h-5" />,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Overall Score & Radar Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Overall Score */}
        <div className="card text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-primary-600 to-purple-600 rounded-full mb-4">
              <span className="text-3xl font-bold text-white">{analysis.overallScore || 70}</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Advanced Analysis Score</h2>
            <p className="text-gray-600">Comprehensive multi-dimensional evaluation</p>
          </div>

          {/* Engagement Forecast */}
          {analysis.engagementForecast && (
            <div className="grid grid-cols-1 gap-4 mt-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Engagement Forecast</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expected Views:</span>
                    <span className="font-medium">{analysis.engagementForecast.expectedViews}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Engagement Rate:</span>
                    <span className="font-medium">{analysis.engagementForecast.engagementRate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Viral Potential:</span>
                    <span className="font-medium capitalize">{analysis.engagementForecast.viralPotential}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Radar Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">Performance Radar</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="category" />
                <PolarRadiusAxis domain={[0, 10]} />
        <Radar
          name="Score"
          dataKey="score"
          stroke={analysis.overallScore >= 8 ? '#10b981' : analysis.overallScore >= 5 ? '#f59e0b' : '#ef4444'}
          fill={analysis.overallScore >= 8 ? '#10b981' : analysis.overallScore >= 5 ? '#f59e0b' : '#ef4444'}
          fillOpacity={0.3}
        />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detailed Analysis Categories */}
      <div className="space-y-6">
        <h3 className="text-2xl font-bold text-gray-900 text-center">Detailed Analysis</h3>
        
        {categories.map((category) => (
          <div key={category.key} className={`card ${category.borderColor} border-l-4`}>
            <button
              onClick={() => toggleCategory(category.key)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className={`flex items-center justify-center w-10 h-10 ${category.bgColor} rounded-lg`}>
                  <span className={category.color}>{category.icon}</span>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{category.title}</h4>
                  <p className="text-sm text-gray-600">
                    {analysis[category.key] ? 
                      `${Object.keys(analysis[category.key]).length} criteria analyzed` : 
                      'Analysis pending'
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {analysis[category.key] && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(
                    Object.values(analysis[category.key]).reduce((sum, item) => sum + item.score, 0) / Object.keys(analysis[category.key]).length
                  )}`}>
                    {getScoreIcon(Object.values(analysis[category.key]).reduce((sum, item) => sum + item.score, 0) / Object.keys(analysis[category.key]).length)}
                    <span className="ml-1">
                      {Math.round(Object.values(analysis[category.key]).reduce((sum, item) => sum + item.score, 0) / Object.keys(analysis[category.key]).length)}
                    </span>
                  </span>
                )}
                {expandedCategories[category.key] ? 
                  <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                }
              </div>
            </button>

            {expandedCategories[category.key] && analysis[category.key] && (
              <div className="px-4 pb-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Detailed Scores Chart */}
                  <div>
                    <h5 className="font-medium text-gray-900 mb-4">Score Breakdown</h5>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={getDetailedScores(category.key)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis domain={[0, 10]} />
                          <Tooltip />
                          <Bar dataKey="score" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Detailed Analysis */}
                  <div>
                    <h5 className="font-medium text-gray-900 mb-4">Detailed Analysis</h5>
                    <div className="space-y-4">
                      {Object.entries(analysis[category.key]).map(([key, value]) => (
                        <div key={key} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h6 className="font-medium text-gray-900 capitalize">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </h6>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(value.score)}`}>
                              {value.score}/10
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{value.analysis}</p>
                          <p className="text-xs text-primary-600 font-medium">
                            💡 {value.recommendations}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Top Recommendations */}
      {(analysis.topStrengths || analysis.topImprovements || analysis.priorityRecommendations) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {analysis.topStrengths && (
            <div className="card">
              <div className="flex items-center mb-4">
                <Zap className="w-5 h-5 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Top Strengths</h3>
              </div>
              <ul className="space-y-2">
                {analysis.topStrengths.map((strength, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.topImprovements && (
            <div className="card">
              <div className="flex items-center mb-4">
                <Target className="w-5 h-5 text-yellow-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Areas for Improvement</h3>
              </div>
              <ul className="space-y-2">
                {analysis.topImprovements.map((improvement, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">{improvement}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.priorityRecommendations && (
            <div className="card">
              <div className="flex items-center mb-4">
                <Award className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Priority Recommendations</h3>
              </div>
              <ul className="space-y-2">
                {analysis.priorityRecommendations.map((recommendation, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">{recommendation}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedAnalysisResults;
